import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Container,
  Box,
  Autocomplete,
  Modal,
  Typography,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSelector, useDispatch } from "react-redux";
import { fetchThingDetails } from "../../../../features/thing/thingThunk";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { setIsVehicle } from "../../../../features/vehicle/vehicleSlice";
import axios from "axios";

const CameraForm = () => {
  const dispatch = useDispatch();
  const { things, loading, error } = useSelector((state) => state.things);
  let isVehicle = useSelector((state) => state.vehicle.isVehicle) || "";

  const [formData, setFormData] = useState({
    vehicleId: "",
    type: "Broadcasting",
    cameras: [{ cameraId: "", cameraName: "" }],
  });

  const [rtmpData, setRtmpData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // success | error | warning | info
  });

  useEffect(() => {
    dispatch(fetchThingDetails());
  }, [dispatch]);

  useEffect(() => {
    if (things && isVehicle) {
      const selectedThing = things.find((e) => e.id === isVehicle);
      if (selectedThing) {
        setFormData((prev) => ({
          ...prev,
          vehicleId: selectedThing.thingId || "",
        }));
      }
    }
  }, [things, isVehicle]);

  const handleCameraChange = (index, field, value) => {
    const updated = [...formData.cameras];
    updated[index][field] = value;
    setFormData({ ...formData, cameras: updated });
  };

  const addCamera = () => {
    setFormData({
      ...formData,
      cameras: [...formData.cameras, { cameraId: "", cameraName: "" }],
    });
  };

  const removeCamera = (index) => {
    const updated = [...formData.cameras];
    updated.splice(index, 1);
    setFormData({ ...formData, cameras: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    const payload = {
      vehicleId: formData.vehicleId,
      type: formData.type,
      cameras: formData.cameras,
    };

    try {
      const res = await axios.post("http://camera.dhundhoo.com/api/devices/createDevice", payload, {
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
      });

      setRtmpData(res.data.cameras || []);
      setModalOpen(true);

      // Success snackbar
      setSnackbar({
        open: true,
        message: "Stream created successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Submit Error:", err.response?.data || err);

      // Error snackbar
      setSnackbar({
        open: true,
        message: "Error creating stream",
        severity: "error",
      });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({
      open: true,
      message: "Copied to clipboard!",
      severity: "info",
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <Container maxWidth="md" sx={{ padding: 3 }}>
      <MDBox
        sx={{
          width: "100%",
          backgroundColor: "white",
          padding: 3,
          borderRadius: 2,
          boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
        }}
      >
        <form onSubmit={handleSubmit}>
          {/* Vehicle Selection */}
          <Autocomplete
            freeSolo
            options={things ? things.map((t) => t.name) : []}
            value={
              things?.find((t) => t.thingId === formData.vehicleId)?.name || ""
            }
            onChange={(event, newValue) => {
              const selectedThing = things.find((t) => t.name === newValue);
              if (selectedThing) {
                dispatch(setIsVehicle(selectedThing.id));
                setFormData({
                  ...formData,
                  vehicleId: selectedThing.thingId,
                });
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Select Vehicle" fullWidth margin="normal" />
            )}
          />

          <TextField
            label="Vehicle ID (thingId)"
            value={formData.vehicleId}
            fullWidth
            margin="normal"
            InputProps={{ readOnly: true }}
          />

          {/* Cameras Section */}
          <Box sx={{ marginTop: 3 }}>
            <h3>Cameras</h3>
            {formData.cameras.map((cam, index) => (
              <Box
                key={index}
                sx={{ display: "flex", gap: 2, marginBottom: 2, alignItems: "center" }}
              >
                <TextField
                  label="Camera ID"
                  value={cam.cameraId}
                  onChange={(e) => handleCameraChange(index, "cameraId", e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Camera Name"
                  value={cam.cameraName}
                  onChange={(e) => handleCameraChange(index, "cameraName", e.target.value)}
                  fullWidth
                />
                <Button
                  color="error"
                  onClick={() => removeCamera(index)}
                  disabled={formData.cameras.length === 1}
                >
                  Remove
                </Button>
              </Box>
            ))}

            <Button
              variant="contained"
              onClick={addCamera}
              sx={{ backgroundColor: "#FB8C00", color: "white !important", paddingX: 4 }}
            >
              +
            </Button>
          </Box>

          {/* Submit Button */}
          <MDBox sx={{ marginTop: 4 }}>
            <MDButton
              type="submit"
              variant="contained"
              sx={{ backgroundColor: "#FB8C00", color: "white !important", paddingX: 4 }}
            >
              Create Stream
            </MDButton>
          </MDBox>
        </form>

        {/* Modal for RTMP URLs */}
        <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography variant="h6" gutterBottom>
              RTMP URLs
            </Typography>
            <Typography variant="body2" gutterBottom>
              Here is your RTMP URL(s), put it in your application:
            </Typography>
            {rtmpData.map((cam) => (
              <Box
                key={cam.streamId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mt: 1,
                  mb: 1,
                  p: 1,
                  bgcolor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2">{cam.rtmpURL}</Typography>
                <IconButton onClick={() => copyToClipboard(cam.rtmpURL)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button onClick={() => setModalOpen(false)} sx={{ mt: 2 }} fullWidth>
              Close
            </Button>
          </Box>
        </Modal>
      </MDBox>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CameraForm;
